import React from "react";
import EventImageField from "./EventImageField";
import EventPointListField from "./EventPointListField";
import {
    buttonStyle,
    cancelStyle,
    formContainerStyle,
    charCountStyle,
    inputStyle,
    inputStyle1,
    labelStyle,
    labelStyle1,
    timeCellStyle,
    timeRowStyle
} from "./eventStyles";
import { getEventTypeOptions } from "./eventFormUtils";

/** Select values 1–100 for male/female slot dropdowns. */
const slotOptions = Array.from({ length: 100 }, (_, index) => index + 1);
/** Select values 18–100 for age range dropdowns. */
const ageOptions = Array.from({ length: 100 - 18 + 1 }, (_, index) => index + 18);

const APP_TITLE_MAX_LENGTH = 25;
const APP_DESCRIPTION_MAX_LENGTH = 90;

/**
 * Presentational create/edit form for events: wires inputs to parent callbacks and composes image + list fields.
 * Extracted from `Event.js` so the tab handles data fetching/state while this file only renders fields and layout.
 */
const EventForm = ({
    appName,
    cities,
    formValues,
    isCitiesLoading,
    isEdit,
    onFieldChange,
    onImageChange,
    onAddPoint,
    onRemovePoint,
    onSubmit,
    onReset,
    onCancel
}) => (
    <div>
        <h1>{isEdit ? 'Edit' : 'Create'} Event</h1>
        <form
            onSubmit={(event) => {
                event.preventDefault();
                onSubmit();
            }}
        >
            <div className="container" style={formContainerStyle}>
                <label style={labelStyle}>Event Name:</label>
                <input
                    type="text"
                    value={formValues.eventName}
                    onChange={(event) => onFieldChange('eventName', event.target.value)}
                    style={inputStyle}
                    required
                    disabled={isEdit}
                />

                <label style={labelStyle}>Event Type:</label>
                <select
                    value={formValues.eventType}
                    onChange={(event) => onFieldChange('eventType', event.target.value)}
                    style={inputStyle}
                    required={!isEdit}
                >
                    <option value="">
                        {isEdit ? 'Keep existing / select type' : 'Select event type'}
                    </option>
                    {getEventTypeOptions(appName).map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>

                <label style={labelStyle}>Event web title:</label>
                <input
                    type="text"
                    value={formValues.webTitle}
                    onChange={(event) => onFieldChange('webTitle', event.target.value)}
                    style={inputStyle}
                    required
                />

                <label style={labelStyle}>Event web description:</label>
                <input
                    type="text"
                    value={formValues.webDescription}
                    onChange={(event) => onFieldChange('webDescription', event.target.value)}
                    style={inputStyle}
                    required
                />

                <label style={labelStyle}>App Title:</label>
                <input
                    type="text"
                    value={formValues.appTitle}
                    onChange={(event) =>
                        onFieldChange(
                            'appTitle',
                            event.target.value.slice(0, APP_TITLE_MAX_LENGTH)
                        )
                    }
                    maxLength={APP_TITLE_MAX_LENGTH}
                    style={inputStyle}
                    required
                />
                <span style={charCountStyle}>
                    {formValues.appTitle.length}/{APP_TITLE_MAX_LENGTH}
                </span>

                <label style={labelStyle}>App Description:</label>
                <input
                    type="text"
                    value={formValues.appDescription}
                    onChange={(event) =>
                        onFieldChange(
                            'appDescription',
                            event.target.value.slice(0, APP_DESCRIPTION_MAX_LENGTH)
                        )
                    }
                    maxLength={APP_DESCRIPTION_MAX_LENGTH}
                    style={inputStyle}
                    required
                />
                <span style={charCountStyle}>
                    {formValues.appDescription.length}/{APP_DESCRIPTION_MAX_LENGTH}
                </span>

                <label style={labelStyle}>You are invited:</label>
                <input
                    type="text"
                    value={formValues.youAreInvited}
                    onChange={(event) => onFieldChange('youAreInvited', event.target.value)}
                    style={inputStyle}
                    required
                />

                <label style={labelStyle}>Button Text 1:</label>
                <input
                    type="text"
                    value={formValues.buttonText1}
                    onChange={(event) => onFieldChange('buttonText1', event.target.value)}
                    style={inputStyle}
                    required
                />

                <label style={labelStyle}>Button Text 2:</label>
                <input
                    type="text"
                    value={formValues.buttonText2}
                    onChange={(event) => onFieldChange('buttonText2', event.target.value)}
                    style={inputStyle}
                    required
                />

                <label style={labelStyle}>Template Slug:</label>
                <input
                    type="text"
                    value={formValues.templateSlug}
                    onChange={(event) => onFieldChange('templateSlug', event.target.value)}
                    style={inputStyle}
                    required
                />

                <label style={labelStyle}>Email Subject:</label>
                <input
                    type="text"
                    value={formValues.emailSubject}
                    onChange={(event) => onFieldChange('emailSubject', event.target.value)}
                    style={inputStyle}
                    required
                />

                <label style={labelStyle}>iOS Version:</label>
                <input
                    type="text"
                    value={formValues.iosVersion}
                    onChange={(event) => onFieldChange('iosVersion', event.target.value)}
                    style={inputStyle}
                    required
                />

                <label style={labelStyle}>Android Version:</label>
                <input
                    type="text"
                    value={formValues.androidVersion}
                    onChange={(event) => onFieldChange('androidVersion', event.target.value)}
                    style={inputStyle}
                    required
                />

                <label style={labelStyle}>Male slots:</label>
                <select
                    onChange={(event) => onFieldChange('maleSlots', event.target.value)}
                    style={inputStyle}
                    required
                    value={formValues.maleSlots}
                >
                    {slotOptions.map((slot) => (
                        <option key={`male-${slot}`} value={slot}>
                            {slot}
                        </option>
                    ))}
                </select>

                <label style={labelStyle}>Female slots:</label>
                <select
                    onChange={(event) => onFieldChange('femaleSlots', event.target.value)}
                    style={inputStyle}
                    required
                    value={formValues.femaleSlots}
                >
                    {slotOptions.map((slot) => (
                        <option key={`female-${slot}`} value={slot}>
                            {slot}
                        </option>
                    ))}
                </select>

                <label style={labelStyle}>Male Min Age:</label>
                <select
                    onChange={(event) => onFieldChange('minMaleAge', event.target.value)}
                    style={inputStyle}
                    required
                    value={formValues.minMaleAge}
                >
                    {ageOptions.map((age) => (
                        <option key={`min-male-${age}`} value={age}>
                            {age}
                        </option>
                    ))}
                </select>

                <label style={labelStyle}>Male Max Age:</label>
                <select
                    onChange={(event) => onFieldChange('maxMaleAge', event.target.value)}
                    style={inputStyle}
                    required
                    value={formValues.maxMaleAge}
                >
                    {ageOptions.map((age) => (
                        <option key={`max-male-${age}`} value={age}>
                            {age}
                        </option>
                    ))}
                </select>

                <label style={labelStyle}>Female Min Age:</label>
                <select
                    onChange={(event) => onFieldChange('minFemaleAge', event.target.value)}
                    style={inputStyle}
                    required
                    value={formValues.minFemaleAge}
                >
                    {ageOptions.map((age) => (
                        <option key={`min-female-${age}`} value={age}>
                            {age}
                        </option>
                    ))}
                </select>

                <label style={labelStyle}>Female Max Age:</label>
                <select
                    onChange={(event) => onFieldChange('maxFemaleAge', event.target.value)}
                    style={inputStyle}
                    required
                    value={formValues.maxFemaleAge}
                >
                    {ageOptions.map((age) => (
                        <option key={`max-female-${age}`} value={age}>
                            {age}
                        </option>
                    ))}
                </select>

                <EventImageField
                    label="Upload Image for App pop up:"
                    previewSrc={formValues.appImagePreview}
                    onChange={(event) => onImageChange('app', event.target.files?.[0])}
                    required={!isEdit}
                />

                <EventImageField
                    label="Upload Image for Web:"
                    previewSrc={formValues.webImagePreview}
                    onChange={(event) => onImageChange('web', event.target.files?.[0])}
                    required={!isEdit}
                />

                <label style={labelStyle}>Event Date:</label>
                <input
                    type="date"
                    value={formValues.eventDate}
                    onChange={(event) => onFieldChange('eventDate', event.target.value)}
                    style={inputStyle}
                    required
                />

                <label style={labelStyle}>Event Time:</label>
                <div style={timeRowStyle}>
                    <div style={timeCellStyle}>
                        <label style={labelStyle1}>From:</label>
                        <input
                            type="time"
                            value={formValues.timeFrom}
                            onChange={(event) => onFieldChange('timeFrom', event.target.value)}
                            style={inputStyle1}
                            required
                        />
                    </div>
                    <div style={timeCellStyle}>
                        <label style={labelStyle1}>To:</label>
                        <input
                            type="time"
                            value={formValues.timeTo}
                            onChange={(event) => onFieldChange('timeTo', event.target.value)}
                            style={inputStyle1}
                            required
                        />
                    </div>
                </div>

                <label style={labelStyle}>City:</label>
                <select
                    value={formValues.offlineEventCityId}
                    onChange={(event) => onFieldChange('offlineEventCityId', event.target.value)}
                    style={inputStyle}
                    required
                    disabled={isCitiesLoading || cities.length === 0}
                >
                    <option value="">
                        {isCitiesLoading ? 'Loading cities...' : 'Select city'}
                    </option>
                    {cities.map((city) => (
                        <option key={city.id} value={city.id}>
                            {city.city_name}
                        </option>
                    ))}
                </select>

                <label style={labelStyle}>Event Location:</label>
                <input
                    type="text"
                    value={formValues.eventLocation}
                    onChange={(event) => onFieldChange('eventLocation', event.target.value)}
                    style={inputStyle}
                    required
                />

                <label style={labelStyle}>Male Base Amount:</label>
                <input
                    type="number"
                    value={formValues.maleBaseAmount}
                    style={inputStyle}
                    readOnly
                    placeholder="Select an event type"
                />

                <label style={labelStyle}>Male Discount:</label>
                <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formValues.maleDiscount}
                    onChange={(event) => onFieldChange('maleDiscount', event.target.value)}
                    style={inputStyle}
                    placeholder="Male discount"
                    disabled={!formValues.eventType}
                />

                <label style={labelStyle}>Male Amount:</label>
                <input
                    type="number"
                    value={formValues.maleAmount}
                    style={inputStyle}
                    readOnly
                    placeholder="Final male amount"
                />

                <label style={labelStyle}>Female Base Amount:</label>
                <input
                    type="number"
                    value={formValues.femaleBaseAmount}
                    style={inputStyle}
                    readOnly
                    placeholder="Select an event type"
                />

                <label style={labelStyle}>Female Discount:</label>
                <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formValues.femaleDiscount}
                    onChange={(event) => onFieldChange('femaleDiscount', event.target.value)}
                    style={inputStyle}
                    placeholder="Female discount"
                    disabled={!formValues.eventType}
                />

                <label style={labelStyle}>Female Amount:</label>
                <input
                    type="number"
                    value={formValues.femaleAmount}
                    style={inputStyle}
                    readOnly
                    placeholder="Final female amount"
                />

                <label style={labelStyle}>Note:</label>
                <input
                    type="text"
                    value={formValues.note}
                    onChange={(event) => onFieldChange('note', event.target.value)}
                    style={inputStyle}
                    required
                />

                <EventPointListField
                    label="What's Included:"
                    draftValue={formValues.newWhatsIncludedPoint}
                    placeholder="Add a point"
                    points={formValues.whatsIncludedPoints}
                    onDraftChange={(value) => onFieldChange('newWhatsIncludedPoint', value)}
                    onAdd={() => onAddPoint('whatsIncludedPoints', 'newWhatsIncludedPoint')}
                    onRemove={(index) => onRemovePoint('whatsIncludedPoints', index)}
                />

                <EventPointListField
                    label="Important Information:"
                    draftValue={formValues.newImportantInformationPoint}
                    placeholder="Add a point"
                    points={formValues.importantInformationPoints}
                    onDraftChange={(value) => onFieldChange('newImportantInformationPoint', value)}
                    onAdd={() => onAddPoint('importantInformationPoints', 'newImportantInformationPoint')}
                    onRemove={(index) => onRemovePoint('importantInformationPoints', index)}
                />

                <EventPointListField
                    label="Terms and Conditions:"
                    draftValue={formValues.newTermsAndConditionPoint}
                    placeholder="Add a point"
                    points={formValues.termsAndConditionPoints}
                    onDraftChange={(value) => onFieldChange('newTermsAndConditionPoint', value)}
                    onAdd={() => onAddPoint('termsAndConditionPoints', 'newTermsAndConditionPoint')}
                    onRemove={(index) => onRemovePoint('termsAndConditionPoints', index)}
                />

                <button type="submit" style={buttonStyle}>
                    {isEdit ? 'Save Changes' : 'Create Event'}
                </button>
                <button type="button" onClick={onReset} style={cancelStyle}>
                    Reset Form
                </button>
                <button type="button" onClick={onCancel} style={cancelStyle}>
                    Cancel
                </button>
            </div>
        </form>
    </div>
);

export default EventForm;
